import { ChakraProvider, Flex, Link, Text } from "@chakra-ui/react";
import "moment/dist/locale/nb";
import React, { ReactNode, useLayoutEffect } from "react";
import ReactDOM from "react-dom/client";
import { Trans, useTranslation } from "react-i18next";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { setIntercomUnreadCount } from "./common/support/help/intercomUnread";
import "./i18n";
import { App } from "./app";
import { BundleVersionWatcher } from "./common/bundle-version/BundleVersionWatcher";
import { ErrorBoundary } from "./common/errors/ErrorBoundary/ErrorBoundary";
import { store } from "./common/redux/store";
import { bundleCommit, theme as chakraTheme, requireStringEnvVar } from "./config";
import { SignedOutDuringSaveNotice } from "./common/auth/SignedOutDuringSaveNotice";
import { ddRum, rumBeforeSend } from "./datadog";

ddRum.init({
  applicationId: requireStringEnvVar("VITE_DATADOG_APPLICATION_ID"),
  clientToken: requireStringEnvVar("VITE_DATADOG_CLIENT_TOKEN"),
  site: "datadoghq.eu",
  service: "sm-webapp",
  env: requireStringEnvVar("VITE_DATADOG_ENV"),
  version: bundleCommit,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
  beforeSend: rumBeforeSend,
});

const ScrollToTopWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return <>{children}</>;
};

const ApplicationErrorFallback = () => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      minH="100vh"
      p={8}
    >
      <Text fontSize="lg" mb={4}>
        {t("Sorry, that was not supposed to happen. Our team has been notified.")}
      </Text>
      <Text>
        <Trans
          i18nKey="You can <1>click here</1> to go back to the home page."
          components={{ 1: <Link color="smPrimary" href="/" /> }}
        />
      </Text>
    </Flex>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <ChakraProvider theme={chakraTheme}>
    <ErrorBoundary
      fallback={<ApplicationErrorFallback />}
      scope="application"
    >
      <Provider store={store}>
        <React.StrictMode>
          <BrowserRouter>
            <BundleVersionWatcher bundleCommit={bundleCommit} />
            <SignedOutDuringSaveNotice />
            <ScrollToTopWrapper>
              <IntercomProvider
                appId={requireStringEnvVar("VITE_INTERCOM_APP_ID")}
                apiBase={requireStringEnvVar("VITE_INTERCOM_BASE_URL")}
                autoBoot={true}
                onUnreadCountChange={setIntercomUnreadCount}
              >
                <App />
              </IntercomProvider>
            </ScrollToTopWrapper>
          </BrowserRouter>
        </React.StrictMode>
      </Provider>
    </ErrorBoundary>
  </ChakraProvider>
);
